<div
  class="h-100 base-slide animate__animated"
  [class.slide-hidden]="slideHidden()"
  [ngClass]="!skipAnimationForHost && !slideHidden() && animation"
  [id]="data.id"
  [style.animationDelay.ms]="animationDelay"
  #container>

  @switch (data.bundle) {
    @case (slideTypes.SlideTextFlipcards) {
      <lib-question-hint [hint]="'SLIDES.click_on_cards_to_explore_other_side' | translate"></lib-question-hint>
      <app-flipcard-group
        [slideData]="data"
        [isRtl]="isRtl"
        [animationEnabled]="!slideHidden()"
        (interactionCompleted)="slideInteractionCompleted({ slide: data, completionRatio: $event })">
      </app-flipcard-group>
    }

    @case (slideTypes.SlideText) {
      @if (isSlideBuilder) {
        <app-slide-text
          [slideData]="data"
          [animationEnabled]="!slideHidden()"
          [isQuiz]="isQuiz">
        </app-slide-text>
      }
    }

    @case (slideTypes.SlideTextImage) {
      <app-slide-image
        [slideData]="data"
        [watermarkEnabled]="imageWatermarkEnabled"
        [preventImageDownload]="preventImageDownload">
      </app-slide-image>
    }

    @case (slideTypes.SlideImage) {
      <app-slide-image
        [slideData]="data"
        [watermarkEnabled]="imageWatermarkEnabled"
        [preventImageDownload]="preventImageDownload">
      </app-slide-image>
    }

    @case (slideTypes.SlideTextVideo) {
      <app-slide-text-video
        [maxVideoHeight]="adjustedChapterContainerHeight"
        [slideData]="data"
        [requiredToWatchVideo]="isVideoRequired"
        (videoStarted)="videoStarted()"
        (videoWatched)="emitVideoWatched()">
      </app-slide-text-video>
    }

    @case (slideTypes.SlideInteractiveVideo) {
      <app-slide-text-video
        [maxVideoHeight]="adjustedChapterContainerHeight"
        [slideData]="data"
        [requiredToWatchVideo]="isVideoRequired"
        (videoStarted)="videoStarted()"
        (videoWatched)="emitVideoWatched()">
      </app-slide-text-video>
    }

    @case (slideTypes.SlideTextAudio) {
      <app-slide-text-audio
        [requiredToCompleteAudio]="isInteractionRequired"
        [brandColors]="brandColors"
        (audioEnded)="slideInteractionCompleted({ slide: data, completionRatio: 1 })"
        [slideData]="data">
      </app-slide-text-audio>
    }

    @case (slideTypes.Hotspots) {
      <lib-hotspots-widget
        [hotspots]="data?.hotspots"
        [id]="data?.id"
        [langcode]="data?.langcode"
        [spacing]="data?.spacing"
        (interactionCompleted)="slideInteractionCompleted({ slide: data, completionRatio: $event })">
      </lib-hotspots-widget>
    }

    @case (slideTypes.SlideAccordion) {
      <lib-accordion-widget
        [accordion]="data?.accordion"
        [animationOptions]="{ enabled: !slideHidden(), animation: data.animation, delay: data.animationDelay }"
        [spacing]="data.spacing"
        [border]="!data.removeBorder"
        [shadow]="!data.removeShadow"
        [openState]="data?.openState"
        (interactionCompleted)="slideInteractionCompleted({ slide: data, completionRatio: $event })">
      </lib-accordion-widget>
    }

    @case (slideTypes.SlideBuckets) {
      <app-slide-buckets
        [slideData]="data"
        [animationEnabled]="!slideHidden()"
        (interactionCompleted)="slideInteractionCompleted({ slide: data, completionRatio: $event })">
      </app-slide-buckets>
    }

    @case (slideTypes.SlideScormPackage) {
      <app-slide-scorm-package
        class="d-block w-100 h-100 flex-grow-1"
        [id]="data.id"
        [isQuiz]="isQuiz"
        [slideCompleted]="data.isCompleted"
        [document]="data.packagePath">
      </app-slide-scorm-package>
    }

    @case (slideTypes.SlideEmbeddedDocument) {
      @if (data.packagePath) {
        <app-document-preview
          [style.height.px]="parentContainerHeight && adjustedChapterContainerHeight"
          [class.full-height]="!parentContainerHeight"
          [class.mb-5]="data.iframeUrl"
          class="d-block w-100 base-slide__document flex-grow-1"
          [document]="data.packagePath"
          [forceFullScreen]="forceFullScreen">
        </app-document-preview>
      }

      @if (data.iframeUrl) {
        <app-iframe-fullscreen
          [style.height.px]="parentContainerHeight && adjustedChapterContainerHeight"
          [class.full-height]="!parentContainerHeight"
          class="d-block w-100 base-slide__document flex-grow-1"
          [url]="data.iframeUrl">
        </app-iframe-fullscreen>
      }
    }

    @case (slideTypes.SlideWebinar) {
      <app-slide-webinar
        [slideData]="data"
        class="d-block w-100 h-100 flex-grow-1">
      </app-slide-webinar>
    }

    @case (slideTypes.SlideBuilder) {
      <app-slide-builder
        class="d-block w-100 flex-grow-1"
        [ngClass]="{ 'h-100': data.heightBehavior !== heightModes.NONE }"
        [slideData]="data"
        [requiredToWatchVideo]="requiredToWatchVideo"
        [chapterContainerHeight]="chapterContainerHeight"
        [requiredToCompleteInteraction]="requiredToCompleteInteraction"
        (videoWatched)="emitVideoWatched($event, false)"
        (interactionCompleted)="slideInteractionCompleted($event)">
      </app-slide-builder>
    }

    @case (slideTypes.SlideDocuments) {
      <app-slide-documents
        [slideData]="data"
        [documentDownload]="libraryDocumentDownload">
      </app-slide-documents>
    }

    @case (slideTypes.SlideSpacer) {
      <app-slide-spacer
        [slideData]="data">
      </app-slide-spacer>
    }

    @case (slideTypes.SlideButton) {
      <lib-button-widget
        [buttonText]="data.link?.title"
        [buttonPosition]="data.buttonPosition"
        [buttonTextColor]="data.buttonTextColor"
        [buttonColor]="data.buttonColor"
        [buttonBlur]="data.buttonBlur"
        [buttonLink]="data.link?.uri">
      </lib-button-widget>
    }

    @case (slideTypes.SlideLearningPaths) {
      <lib-learning-paths-widget
        [paths]="data.contents"
        [title]="data?.subTitle"
        [langcode]="data?.langcode"
        [textColor]="data.defaultTextColor"
        (navigateToPath)="navigateToPath($event)"
        (startPath)="startPath($event)">
      </lib-learning-paths-widget>
    }

    @case (slideTypes.SlideChallenges) {
      <app-slide-challenges
        [data]="data">
      </app-slide-challenges>
    }

    @case (slideTypes.SlideContentHighlight) {
      <lib-content-highlight-widget
        [title]="data?.subTitle"
        [items]="data?.automaticContentItems?.length ? data?.automaticContentItems : data?.contents"
        [textColor]="data?.defaultTextColor"
        [langcode]="data?.langcode"
        [sliderEnabled]="data?.sliderEnabled"
        [mobileCardStyle]="data?.mobileCardStyle"
        [isPaymentEnabled]="isPaymentEnabled"
        [courseDetailsEnabled]="courseDetailsEnabled"
        (openDetails)="openDetails($event)"
        (navigateToEntity)="navigate($event)">
      </lib-content-highlight-widget>
    }

    @case (slideTypes.SlideInstaLearnList) {
      <app-slide-insta-learn-posts
        [data]="data">
      </app-slide-insta-learn-posts>
    }

    @case (slideTypes.SlideInstaLearnTopicList) {
      <app-slide-insta-learn-topics
        [data]="data">
      </app-slide-insta-learn-topics>
    }

    @case (slideTypes.SlideCarousel) {
      <app-slide-carousel
        [data]="data"
        (interactionCompleted)="slideInteractionCompleted({ slide: data, completionRatio: $event })">
      </app-slide-carousel>
    }

    @case (slideTypes.SlideSocialWall && data?.social_wall?.length) {
      <app-slide-social-wall
        [data]="data">
      </app-slide-social-wall>
    }
  }
</div>
